import { logoutUser } from '../api';
import { getSessionId, getAuthType, clearSession } from './authStorage';
import { getHostUri } from './authHelpers';
import { createLogger } from '../utils/logging';
import { closeTab } from '../utils/closeTab';

const logger = createLogger('SignOut');

const OHID = 'ohid';

/**
 * Invalidate the current session, client- and server-side. Does not navigate
 * or close anything - callers decide what happens once the session is dead.
 */
export async function terminateSession(): Promise<void> {
  const sessionId = getSessionId();

  clearSession();

  if (!sessionId) {
    return;
  }

  try {
    // Prevent double sign-out
    if (!sessionStorage.getItem('logout')) {
      sessionStorage.setItem('logout', 'true');
      await logoutUser();
    }
  } catch (error) {
    logger.error('Sign Out Failed', 'Error clearing server session', { error });
  }
}

/**
 * External (OHID/Benefit Central) users never see the login screen on
 * sign-out/timeout - their tab is closed instead, leaving the Benefit Central
 * session untouched. All other auth types keep the redirect-to-login behavior.
 * authType must be read before terminateSession() clears it from storage.
 */
export async function performSignOut(): Promise<void> {
  const authType = getAuthType();
  const redirectUri = getHostUri();

  try {
    await terminateSession();
  } finally {
    if (authType === OHID) {
      // If the browser blocks the close (tab wasn't script-opened, or this
      // fired without a fresh user gesture), navigate to about:blank instead
      // of leaving the terminated session's data rendered on screen.
      closeTab(() => window.location.replace('about:blank'));
    } else {
      window.location.href = redirectUri;
    }
  }
}
